/**
 * Streak calculator.
 *
 * Streaks count consecutive periods where a habit was completed (or protected by a grace day).
 * For daily/specific_days/every_n_days: consecutive due dates.
 * For x_per_week: consecutive weeks meeting the target.
 * For x_per_month: consecutive months meeting the target.
 */

import { addDays, compareDates, getFirstDayOfMonth, getLastDayOfMonth } from "./day-boundary";
import { getDueDatesInRange, getWeekBounds } from "./schedule";

import type { CompletionRecord, PeriodBounds, ScheduleConfig, StreakResult } from "./types";

/**
 * Calculate current and best streaks for a habit.
 *
 * Walks backwards from `endDate` for the current streak,
 * then scans all history for the best streak.
 */
export function calculateStreak(
  config: ScheduleConfig,
  completions: CompletionRecord[],
  startDate: string,
  endDate: string,
  firstDayOfWeek: number,
  graceDaysPerWeek: number,
): StreakResult {
  if (config.type === "x_per_week") {
    return calculatePeriodStreak(config, completions, startDate, endDate, firstDayOfWeek, "week");
  }

  if (config.type === "x_per_month") {
    return calculatePeriodStreak(config, completions, startDate, endDate, firstDayOfWeek, "month");
  }

  return calculateDailyStreak(
    config,
    completions,
    startDate,
    endDate,
    firstDayOfWeek,
    graceDaysPerWeek,
  );
}

/**
 * Streak calculation for daily/specific_days/every_n_days habits.
 * Each due date is a streak unit. Grace days protect the streak.
 */
function calculateDailyStreak(
  config: ScheduleConfig,
  completions: CompletionRecord[],
  startDate: string,
  endDate: string,
  firstDayOfWeek: number,
  graceDaysPerWeek: number,
): StreakResult {
  const context = { habitStartDate: startDate, completions };
  const dueDates = getDueDatesInRange(config, startDate, endDate, context);

  if (dueDates.length === 0) {
    return { current: 0, best: 0 };
  }

  const completionSet = buildCompletionDateSet(completions);
  const skipSet = buildSkipDateSet(completions);

  // Track grace days per week to enforce the limit
  const graceTracker = new Map<string, number>(); // weekStart → graceCount

  // Determine status of each due date: "completed" | "grace" | "broken"
  const statuses: Array<"completed" | "grace" | "broken"> = dueDates.map((date) => {
    if (completionSet.has(date)) return "completed";
    if (skipSet.has(date)) return "grace"; // skips preserve streaks (like grace days)

    // Check if grace day is available
    const week = getWeekBounds(date, firstDayOfWeek);
    const weekKey = week.start;
    const usedThisWeek = graceTracker.get(weekKey) ?? 0;

    if (usedThisWeek < graceDaysPerWeek) {
      graceTracker.set(weekKey, usedThisWeek + 1);
      return "grace";
    }

    return "broken";
  });

  // Current streak: walk backwards from the end
  // Grace days can extend a streak but a streak needs at least one real completion
  let current = 0;
  let hasRealCompletion = false;
  for (let i = statuses.length - 1; i >= 0; i--) {
    if (statuses[i] === "broken") break;
    current++;
    if (statuses[i] === "completed") hasRealCompletion = true;
  }
  if (!hasRealCompletion) current = 0;

  // Best streak: scan forward
  let best = 0;
  let run = 0;
  let runHasCompletion = false;
  for (const s of statuses) {
    if (s !== "broken") {
      run++;
      if (s === "completed") runHasCompletion = true;
      if (runHasCompletion && run > best) best = run;
    } else {
      run = 0;
      runHasCompletion = false;
    }
  }

  return { current, best };
}

/**
 * Streak calculation for x_per_week and x_per_month habits.
 * Each period (week or month) where completedCount >= targetCount counts as one streak unit.
 */
function calculatePeriodStreak(
  config: ScheduleConfig & { type: "x_per_week" | "x_per_month" },
  completions: CompletionRecord[],
  startDate: string,
  endDate: string,
  firstDayOfWeek: number,
  periodType: "week" | "month",
): StreakResult {
  const periods = getAllPeriods(periodType, startDate, endDate, firstDayOfWeek);

  if (periods.length === 0) {
    return { current: 0, best: 0 };
  }

  const completionMap = buildCompletionMap(completions);

  // Determine if each period met the target
  const met: boolean[] = periods.map((period) => {
    const count = countCompletionsInPeriod(completionMap, period);
    return count >= config.count;
  });

  // Current streak: walk backwards
  let current = 0;
  for (let i = met.length - 1; i >= 0; i--) {
    if (met[i]) {
      current++;
    } else {
      break;
    }
  }

  // Best streak: scan forward
  let best = 0;
  let run = 0;
  for (const m of met) {
    if (m) {
      run++;
      if (run > best) best = run;
    } else {
      run = 0;
    }
  }

  return { current, best };
}

/**
 * Get all distinct periods between startDate and endDate.
 */
function getAllPeriods(
  periodType: "week" | "month",
  startDate: string,
  endDate: string,
  firstDayOfWeek: number,
): PeriodBounds[] {
  const periods: PeriodBounds[] = [];
  let current = startDate;

  while (compareDates(current, endDate) <= 0) {
    let bounds: PeriodBounds;

    if (periodType === "week") {
      bounds = getWeekBounds(current, firstDayOfWeek);
    } else {
      bounds = {
        start: getFirstDayOfMonth(current),
        end: getLastDayOfMonth(current),
      };
    }

    // Only include completed periods (end <= endDate), except the current one
    periods.push(bounds);

    // Jump to next period
    current = addDays(bounds.end, 1);
  }

  return periods;
}

/**
 * Count done/backfill completions within a period.
 */
function countCompletionsInPeriod(
  completionMap: Map<string, CompletionRecord[]>,
  period: PeriodBounds,
): number {
  let count = 0;
  let current = period.start;

  while (compareDates(current, period.end) <= 0) {
    const records = completionMap.get(current);
    if (records) {
      for (const r of records) {
        if (r.type === "done" || r.type === "backfill") {
          count++;
        }
      }
    }
    current = addDays(current, 1);
  }

  return count;
}

function buildCompletionDateSet(completions: CompletionRecord[]): Set<string> {
  const set = new Set<string>();
  for (const c of completions) {
    if (c.type === "done" || c.type === "backfill") {
      set.add(c.effective_date);
    }
  }
  return set;
}

function buildSkipDateSet(completions: CompletionRecord[]): Set<string> {
  const set = new Set<string>();
  for (const c of completions) {
    if (c.type === "skip") {
      set.add(c.effective_date);
    }
  }
  return set;
}

function buildCompletionMap(completions: CompletionRecord[]): Map<string, CompletionRecord[]> {
  const map = new Map<string, CompletionRecord[]>();
  for (const c of completions) {
    const existing = map.get(c.effective_date);
    if (existing) {
      existing.push(c);
    } else {
      map.set(c.effective_date, [c]);
    }
  }
  return map;
}
